import React, { useEffect, useState, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Chip,
  Avatar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  InputAdornment,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  Tab,
  Tabs,
  Tooltip,
  Badge,
  Skeleton,
  Alert,
  alpha,
  LinearProgress,
  SelectChangeEvent,
} from '@mui/material';
import {
  Search as SearchIcon,
  FilterList as FilterIcon,
  MoreVert as MoreIcon,
  Visibility as ViewIcon,
  Person as PersonIcon,
  TrendingUp as StatsIcon,
  Refresh as RefreshIcon,
  Download as ExportIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  LocationOn as LocationIcon,
  Work as WorkIcon,
  HealthAndSafety as HealthIcon,
  FamilyRestroom as FamilyIcon,
  Shield as ShieldIcon,
  Badge as BadgeIcon,
  Business as BusinessIcon,
  CheckCircle as ActiveIcon,
} from '@mui/icons-material';
import { RootState } from '../../store';
import axios from 'axios';

// Types
interface OfficerListItem {
  id: number;
  employeeId: string;
  fullName: string;
  email: string;
  mobile: string;
  cadre: string;
  cadreColor: string;
  designation: string;
  department: string;
  postingLocation: string;
  status: string;
  statusColor: string;
  bloodGroup: string;
  profilePhotoUrl: string;
  familyMembersCount: number;
  totalAppointments: number;
  appointmentsThisMonth: number;
  pendingAppointments: number;
  lastAppointmentDate: string;
  hasMedicalInfo: boolean;
  hasEmergencyContact: boolean;
  isVerified: boolean;
  verificationDate: string;
  createdAt: string;
}

interface OfficerStatistics {
  totalOfficers: number;
  activeOfficers: number;
  inactiveOfficers: number;
  onLeaveOfficers: number;
  pendingVerifications: number;
  newThisMonth: number;
  cadreDistribution: { cadre: string; displayName: string; count: number }[];
  departmentDistribution: { department: string; count: number }[];
  locationDistribution: { location: string; count: number }[];
  totalAppointmentsToday: number;
  pendingAppointmentsToday: number;
  completedAppointmentsToday: number;
}

const OfficerManagementPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useSelector((state: RootState) => state.auth);

  const [loading, setLoading] = useState(true);
  const [officers, setOfficers] = useState<OfficerListItem[]>([]);
  const [statistics, setStatistics] = useState<OfficerStatistics | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalElements, setTotalElements] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [cadreFilter, setCadreFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [actionAnchorEl, setActionAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedOfficer, setSelectedOfficer] = useState<OfficerListItem | null>(null);
  const [activeTab, setActiveTab] = useState(0);

  const getCadreIcon = (cadre: string) => {
    switch (cadre) {
      case 'IAS': return '🦁';
      case 'IPS': return '🛡️';
      case 'IFS': return '🌍';
      case 'IRS': return '💰';
      case 'JUDGE': return '⚖️';
      case 'MINISTER': return '🏛️';
      default: return '👤';
    }
  };

  const fetchOfficers = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('accessToken');
      const params = new URLSearchParams({
        page: page.toString(),
        size: rowsPerPage.toString(),
        search: searchQuery,
        cadre: cadreFilter,
        status: statusFilter,
      });

      const response = await axios.get(
        `${import.meta.env.VITE_API_URL}/api/v1/admin/officers?${params}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        setOfficers(response.data.data.content);
        setTotalElements(response.data.data.totalElements);
      }
    } catch (error) {
      console.error('Error fetching officers:', error);
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, searchQuery, cadreFilter, statusFilter]);

  const fetchStatistics = useCallback(async () => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await axios.get(
        `${import.meta.env.VITE_API_URL}/api/v1/admin/officers/statistics`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        setStatistics(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching statistics:', error);
    }
  }, []);

  useEffect(() => {
    fetchOfficers();
    fetchStatistics();
  }, [fetchOfficers, fetchStatistics]);

  const handleActionOpen = (event: React.MouseEvent<HTMLElement>, officer: OfficerListItem) => {
    setActionAnchorEl(event.currentTarget);
    setSelectedOfficer(officer);
  };

  const handleActionClose = () => {
    setActionAnchorEl(null);
    setSelectedOfficer(null);
  };

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const statCards = statistics ? [
    { title: 'Total Officers', value: statistics.totalOfficers, icon: <PersonIcon />, color: '#2563eb' },
    { title: 'Active', value: statistics.activeOfficers, icon: <ActiveIcon />, color: '#10b981' },
    { title: 'New This Month', value: statistics.newThisMonth, icon: <StatsIcon />, color: '#8b5cf6' },
  ] : [];

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700, color: '#1e293b' }}>
            Officer Management
          </Typography>
          <Typography variant="body2" sx={{ color: '#64748b', mt: 0.5 }}>
            Monitor and manage all registered government officers
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={() => { fetchOfficers(); fetchStatistics(); }}
            sx={{ borderRadius: 2, textTransform: 'none' }}
          >
            Refresh
          </Button>
          <Button
            variant="outlined"
            startIcon={<ExportIcon />}
            sx={{ borderRadius: 2, textTransform: 'none' }}
          >
            Export
          </Button>
        </Box>
      </Box>

      {/* Statistics Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {statCards.map((stat, index) => (
          <Grid item xs={12} sm={6} md={3} key={index}>
            <Card sx={{ borderRadius: 2, boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
              <CardContent sx={{ p: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="body2" sx={{ color: '#64748b', fontSize: '0.75rem' }}>
                      {stat.title}
                    </Typography>
                    <Typography variant="h4" sx={{ fontWeight: 700, color: stat.color, mt: 0.5 }}>
                      {stat.value}
                    </Typography>
                  </Box>
                  <Box sx={{
                    p: 1.5,
                    borderRadius: 2,
                    bgcolor: alpha(stat.color, 0.1),
                    color: stat.color,
                  }}>
                    {stat.icon}
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Cadre Distribution */}
      {statistics && statistics.cadreDistribution.length > 0 && (
        <Card sx={{ mb: 3, borderRadius: 2, boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
          <CardContent sx={{ p: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2 }}>
              Cadre Distribution
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {statistics.cadreDistribution.map((item, index) => (
                <Chip
                  key={index}
                  label={`${item.displayName || item.cadre}: ${item.count}`}
                  size="small"
                  sx={{ borderRadius: 1, fontWeight: 500 }}
                  color="primary"
                  variant="outlined"
                />
              ))}
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Tabs */}
      <Paper sx={{ borderRadius: 2, boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
        <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)} sx={{ borderBottom: '1px solid #e2e8f0' }}>
          <Tab label={`All Officers (${totalElements})`} sx={{ textTransform: 'none' }} />
          <Tab label="IAS" sx={{ textTransform: 'none' }} />
          <Tab label="IPS" sx={{ textTransform: 'none' }} />
          <Tab label="IFS" sx={{ textTransform: 'none' }} />
          <Tab label="IRS" sx={{ textTransform: 'none' }} />
        </Tabs>

        {/* Search & Filter */}
        <Box sx={{ p: 2, display: 'flex', gap: 2, alignItems: 'center', borderBottom: '1px solid #e2e8f0' }}>
          <TextField
            size="small"
            placeholder="Search officers..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            sx={{ minWidth: 300 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: '#64748b' }} />
                </InputAdornment>
              ),
            }}
          />
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Cadre</InputLabel>
            <Select
              value={cadreFilter}
              label="Cadre"
              onChange={(e) => setCadreFilter(e.target.value)}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="IAS">IAS</MenuItem>
              <MenuItem value="IPS">IPS</MenuItem>
              <MenuItem value="IFS">IFS</MenuItem>
              <MenuItem value="IRS">IRS</MenuItem>
              <MenuItem value="JUDGE">Judge</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={statusFilter}
              label="Status"
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="ACTIVE">Active</MenuItem>
              <MenuItem value="INACTIVE">Inactive</MenuItem>
              <MenuItem value="ON_LEAVE">On Leave</MenuItem>
            </Select>
          </FormControl>
        </Box>

        {/* Table */}
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: 'transparent' }}>
                <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Officer</TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Cadre</TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Department</TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Location</TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Medical Info</TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Family</TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }} align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={8}>
                    <LinearProgress />
                  </TableCell>
                </TableRow>
              ) : officers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8}>
                    <Box sx={{ p: 4, textAlign: 'center' }}>
                      <Typography sx={{ color: '#64748b' }}>No officers found</Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              ) : (
                officers.map((officer) => (
                  <TableRow key={officer.id} hover>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Avatar sx={{ bgcolor: officer.cadreColor, width: 36, height: 36, fontSize: '0.85rem' }}>
                          {getCadreIcon(officer.cadre)}
                        </Avatar>
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {officer.fullName}
                          </Typography>
                          <Typography variant="caption" sx={{ color: '#64748b' }}>
                            {officer.employeeId}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={officer.cadre}
                        size="small"
                        sx={{
                          bgcolor: alpha(officer.cadreColor, 0.1),
                          color: officer.cadreColor,
                          fontWeight: 600,
                          fontSize: '0.7rem',
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                        {officer.department}
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#64748b' }}>
                        {officer.designation}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <LocationIcon sx={{ fontSize: 14, color: '#64748b' }} />
                        <Typography variant="body2">{officer.postingLocation}</Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={officer.status}
                        size="small"
                        sx={{
                          bgcolor: alpha(officer.statusColor, 0.1),
                          color: officer.statusColor,
                          fontWeight: 500,
                          fontSize: '0.7rem',
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      {officer.hasMedicalInfo ? (
                        <Chip label="Complete" size="small" color="success" sx={{ fontSize: '0.7rem' }} />
                      ) : (
                        <Chip label="Incomplete" size="small" color="warning" sx={{ fontSize: '0.7rem' }} />
                      )}
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <FamilyIcon sx={{ fontSize: 16, color: '#64748b' }} />
                        <Typography variant="body2">{officer.familyMembersCount}</Typography>
                      </Box>
                    </TableCell>
                    <TableCell align="right">
                      <IconButton size="small" onClick={(e) => handleActionOpen(e, officer)}>
                        <MoreIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Pagination */}
        <TablePagination
          component="div"
          count={totalElements}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[10, 25, 50]}
        />
      </Paper>

      {/* Action Menu */}
      <Menu
        anchorEl={actionAnchorEl}
        open={Boolean(actionAnchorEl)}
        onClose={handleActionClose}
      >
        <MenuItem onClick={() => { handleActionClose(); }}>
          <ListItemIcon><ViewIcon fontSize="small" /></ListItemIcon>
          <ListItemText>View Profile</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => { handleActionClose(); }}>
          <ListItemIcon><HealthIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Medical Info</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => { handleActionClose(); }}>
          <ListItemIcon><FamilyIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Family Members</ListItemText>
        </MenuItem>
        <Divider />
        <MenuItem onClick={handleActionClose}>
          <ListItemIcon><EmailIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Send Email</ListItemText>
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default OfficerManagementPage;